import React from "react";

type DataAvailableToggleVariant = "kpi" | "briefing";

const TOGGLE_COPY: Record<
  DataAvailableToggleVariant,
  { label: string; loading: string; allAvailable: string }
> = {
  kpi: {
    label: "KPIs Data Available",
    loading: "Loading KPIs availability. Please wait.",
    allAvailable:
      "All selected accounts already have KPIs data, so this filter is unavailable.",
  },
  briefing: {
    label: "Briefing Available",
    loading: "Loading briefing availability. Please wait.",
    allAvailable:
      "All selected accounts already have briefing data, so this toggle is unavailable.",
  },
};

interface KpiDataAvailableToggleProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  availableCount?: number;
  unavailableCount?: number;
  /** Counts are still loading — keep bar visible, disable the switch. */
  loading?: boolean;
  /** Account Health vs Business Health copy. */
  variant?: DataAvailableToggleVariant;
}

/** Compact availability toggle for Account Health KPIs or Business Health briefings. */
const KpiDataAvailableToggle: React.FC<KpiDataAvailableToggleProps> = ({
  checked,
  onCheckedChange,
  unavailableCount = 0,
  loading = false,
  variant = "kpi",
}) => {
  const copy = TOGGLE_COPY[variant];
  const isToggleDisabled = loading || unavailableCount <= 0;
  // When nothing is unavailable, the filter is a no-op — keep the switch visually on.
  const displayChecked =
    isToggleDisabled && unavailableCount <= 0 ? true : checked;

  const disabledReason = loading
    ? copy.loading
    : unavailableCount <= 0
      ? copy.allAvailable
      : undefined;

  return (
    <div
      className="inline-flex h-8 max-w-full items-center gap-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50/80"
      title={isToggleDisabled ? disabledReason : undefined}
    >
      <label
        className={`flex h-full shrink-0 select-none items-center gap-2 px-3 ${
          isToggleDisabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer hover:bg-white/70"
        }`}
        title={isToggleDisabled ? disabledReason : copy.label}
      >
        <span className="whitespace-nowrap text-xs text-slate-600 font-medium">
          {copy.label}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={displayChecked}
          aria-disabled={isToggleDisabled}
          aria-label={
            isToggleDisabled ? `${copy.label}. ${disabledReason}` : copy.label
          }
          disabled={isToggleDisabled}
          onClick={e => {
            e.preventDefault();
            if (isToggleDisabled) return;
            onCheckedChange(!checked);
          }}
          className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 disabled:cursor-not-allowed ${
            displayChecked ? "bg-purple-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
              displayChecked ? "translate-x-3.5" : "translate-x-0.5"
            }`}
          />
        </button>
      </label>
    </div>
  );
};

export default KpiDataAvailableToggle;
